import { grossUpCardFee, resolveCardFeeConfig, roundToCents } from "utils/bookingCalculations"
import { MIN_PRICE_SAVING } from "../constants"

// The lines a New Booking price summary lists, read from calculateBookingTotal's breakdown: step 1's
// summary (BookingSummary) and step 2's Price Details (OrderSummary).

// Guest lines, the fees the guide lists on their own line (show_as_line_item, as in the guest
// widget), and the rest of Taxes & Fees added up.
export const splitPriceBreakdown = (breakdown = []) => {
  const taxEntry = breakdown.find((item) => item?.isTaxFee)
  const guestLines = breakdown.filter((item) => item && !item.isTaxFee && !item.isPriceChange)
  const listedFees = (taxEntry?.children ?? []).filter((item) => item.isLineItem)
  const otherFeesTotal = taxEntry
    ? (Number(taxEntry.rawAmount) || 0) - listedFees.reduce((sum, item) => sum + (Number(item.rawAmount) || 0), 0)
    : 0
  return { guestLines, listedFees, otherFeesTotal }
}

// An experience's price as its summary shows it: its total before the card fee, the price before the
// date's discount (the total itself when the discount is too small to show), and its add-ons.
export const getItemPrices = (selections) => {
  const total = Number(selections?.preCardTotal ?? selections?.amount) || 0
  const beforeAdjustments = Number(selections?.preCardTotalBeforeAdjustments) || 0
  return {
    total,
    beforeAdjustments: beforeAdjustments - total > MIN_PRICE_SAVING ? beforeAdjustments : total,
    addOnsTotal: Number(selections?.addOnsTotal) || 0
  }
}

// Those prices added up over an order's experiences ({ selections } each).
export const sumItemPrices = (items = []) =>
  items.reduce(
    (sum, item) => {
      const { total, beforeAdjustments, addOnsTotal } = getItemPrices(item.selections)
      return {
        total: sum.total + total,
        beforeAdjustments: sum.beforeAdjustments + beforeAdjustments,
        addOnsTotal: sum.addOnsTotal + addOnsTotal
      }
    },
    { total: 0, beforeAdjustments: 0, addOnsTotal: 0 }
  )

// An order's adjusted subtotal split across its experiences in proportion to their own subtotals,
// in cents, the last taking the remainder so the shares add up to it exactly (equal shares when the
// experiences have no subtotal). Each experience is then priced on its share.
export const splitAdjustedSubtotal = (adjustedSubtotal, subtotals = []) => {
  if (subtotals.length === 0) return []
  const total = subtotals.reduce((sum, value) => sum + value, 0)
  const shares = subtotals.map((value) => roundToCents(total > 0 ? (adjustedSubtotal * value) / total : adjustedSubtotal / subtotals.length))
  const allocated = shares.slice(0, -1).reduce((sum, value) => sum + value, 0)
  shares[shares.length - 1] = roundToCents(adjustedSubtotal - allocated)
  return shares
}

// The card fee on an order paid by card: the order is one card payment, so the backend grosses up
// one fee on its whole total (not one per experience) and rounds it to the cent once, as for a single
// booking (DS-10022: 1183.20 -> 35.65). None unless an experience passes the fee on.
export const getOrderCardFee = (items = [], orderTotal = 0) => {
  const cardFeeConfig = items
    .map(({ experience }) =>
      resolveCardFeeConfig({ customFees: experience?.assigned_custom_fees, platformFeeSetting: experience?.platform_fee_setting })
    )
    .find((config) => config.enabled)
  return cardFeeConfig ? roundToCents(grossUpCardFee(orderTotal, cardFeeConfig)) : 0
}

// "Adults x 2" from the guest type; a flat-rate line keeps its own label.
export const getGuestLineLabel = (item, t) =>
  item.guestType
    ? t("booking.line_quantity", { label: t(`booking.guest_counter.types.${item.guestType}`), count: item.guestCount })
    : item.label

// One line per selected add-on ("Waterproof Dry Bag x 2", price × quantity), priced the same way
// as calculateAddOnsTotal so the lines add up to bookingSelections.addOnsTotal.
export const getAddOnLines = (selections = {}, addOns = []) => {
  if (!selections || !addOns?.length) return []
  const byId = new Map(addOns.map((addOn) => [addOn.id, addOn]))
  return Object.entries(selections).reduce((lines, [id, quantity]) => {
    const addOn = byId.get(Number(id)) || byId.get(id)
    const count = Number(quantity || 0)
    if (!addOn || addOn.enabled === false || count <= 0) return lines
    return [...lines, { key: String(addOn.id), title: addOn.title, count, amount: Number(addOn.price || 0) * count }]
  }, [])
}
